/**
 * NotificationSettings - "tell me when a job comes up"
 * -----------------------------------------------------
 * Two halves, and they are genuinely separate things:
 *
 *   1. PERMISSION - does this browser allow notifications at all? Per device,
 *      granted by the person, and we cannot grant it for them.
 *   2. PREFERENCES - what they want to hear about. Saved on the account, so it
 *      follows them to every device.
 *
 * Somebody can have preferences set and permission refused, or the reverse.
 * The screen says which is which, because "I turned it on and nothing happens"
 * is otherwise impossible to work out.
 *
 * WHY THE BUTTON IS A BUTTON
 * Permission is only ever asked from a real click. A browser ignores a request
 * that did not come from one, and some treat an ignored request as a refusal -
 * which is permanent and cannot be undone from the site. So nothing here runs
 * on page load.
 */

import { useEffect, useState } from 'react';
import { Bell, BellOff, Send, Smartphone, AlertTriangle, Check } from 'lucide-react';

import { authApi, choreApi, pushApi } from '../services/api';
import { pushSupport, askAndSubscribe, unsubscribe, isSubscribed } from '../services/push';
import Badge from './Badge';
import { useI18n } from '../hooks/useI18n';

const QUIET_TIMES = [];
for (let h = 0; h < 24; h += 1) QUIET_TIMES.push(`${String(h).padStart(2, '0')}:00`);
QUIET_TIMES.push('07:30', '21:30');
QUIET_TIMES.sort();

export default function NotificationSettings({ user, onSaved }) {
  const { t } = useI18n();
  const [support, setSupport] = useState({ ok: false, reason: 'checking' });
  const [on, setOn] = useState(false);
  const [serverReady, setServerReady] = useState(true);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const [prefs, setPrefs] = useState({
    enabled: user.alerts?.enabled ?? false,
    categories: user.alerts?.categories || [],
    minPay: user.alerts?.minPay ?? '',
    maxDistanceKm: user.alerts?.maxDistanceKm ?? '',
    quietFrom: user.alerts?.quietFrom || '21:00',
    quietTo: user.alerts?.quietTo || '07:30',
  });

  useEffect(() => {
    setSupport(pushSupport());
    isSubscribed().then(setOn);
    pushApi.key().then((k) => setServerReady(k.available)).catch(() => setServerReady(false));
    choreApi.categories().then((d) => setCategories(d.categories)).catch(() => {});
  }, []);

  const turnOn = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    const result = await askAndSubscribe();
    setBusy(false);

    if (!result.ok) {
      setError(
        {
          refused: t('alerts.refused'),
          blocked: t('alerts.blocked'),
          'ios-needs-install': t('alerts.iosNeedsInstall'),
          'server-off': t('alerts.serverOff'),
          unsupported: t('alerts.unsupported'),
        }[result.reason] || t('alerts.failed')
      );
      return;
    }

    setOn(true);
    // Turning it on here means they want them - saving that with it avoids a
    // state where the phone is subscribed but the account says no.
    await save({ ...prefs, enabled: true });
    setNotice(t('alerts.on'));
  };

  const turnOff = async () => {
    setBusy(true);
    await unsubscribe();
    setBusy(false);
    setOn(false);
    setNotice(t('alerts.off'));
  };

  const sendTest = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await pushApi.test();
      setNotice(t('alerts.testSent'));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const save = async (next = prefs) => {
    setError('');
    try {
      const saved = await authApi.updateProfile({
        alerts: {
          enabled: next.enabled,
          categories: next.categories,
          minPay: next.minPay === '' ? null : Number(next.minPay),
          maxDistanceKm: next.maxDistanceKm === '' ? null : Number(next.maxDistanceKm),
          quietFrom: next.quietFrom,
          quietTo: next.quietTo,
        },
      });
      if (onSaved) onSaved(saved.user);
      setNotice(t('alerts.saved'));
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleCategory = (name) => {
    setPrefs((p) => ({
      ...p,
      categories: p.categories.includes(name)
        ? p.categories.filter((c) => c !== name)
        : [...p.categories, name],
    }));
  };

  return (
    <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
      <div className="row-between" style={{ marginBottom: 'var(--space-xs)' }}>
        <h3>{t('alerts.title')}</h3>
        {on ? <Badge tone="green" icon={Check}>{t('alerts.onHere')}</Badge> : <Badge tone="grey">{t('alerts.offHere')}</Badge>}
      </div>

      <p className="text-sm text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
        {t('alerts.pitch')}
      </p>

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-sm)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      {/* ---- 1. Permission on this device ---- */}
      {support.reason === 'ios-needs-install' ? (
        <div className="alert alert-warning">
          <Smartphone size={20} aria-hidden="true" />
          <div>
            <strong>{t('misc.iosFirstStep')}</strong> {t('alerts.iosStepsBefore')}{' '}
            <strong>{t('alerts.addToHomeScreen')}</strong>{t('alerts.iosStepsAfter')}{' '}
            <a href="#install-guide">{t('misc.fullSteps')}</a>.
          </div>
        </div>
      ) : !serverReady ? (
        <div className="alert alert-warning">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            Notifications are not set up on the server yet. Run <code>npm run make-vapid</code> and
            put the keys in <code>backend/.env</code>.
          </div>
        </div>
      ) : support.reason === 'unsupported' ? (
        /**
         * "This browser cannot show notifications" on its own was the wrong
         * thing to say, and on an iPad it was simply untrue.
         *
         * iPadOS reports itself as a Mac, so the iPad fell past the
         * needs-install branch above and landed here - told it could not do
         * something it does very well, once the site is on the home screen.
         * That detection is fixed in services/push.js, but the message needed
         * fixing too: a flat refusal with no way forward is what somebody
         * remembers, and they do not come back to try again.
         *
         * So this now says the commonest reason and what to do about it, and
         * puts the steps for every device one tap away - because if we were
         * wrong about the device once, we can be wrong again.
         */
        <div className="alert alert-warning">
          <Smartphone size={20} aria-hidden="true" />
          <div>
            <strong>{t('alerts.unavailableHere')}</strong>
            <p style={{ marginTop: 6 }}>
              {t('alerts.commonestReason')}{' '}
              <strong>{t('misc.mustBeInstalled')}</strong> {t('alerts.appleNoSafari')}
            </p>
            <p style={{ marginTop: 6 }}>
              {t('misc.twoMinutes')}{' '}
              <a href="#install-guide">
                <strong>{t('misc.stepByStep')}</strong>
              </a>{' '}
              {t('alerts.deviceList')}
            </p>
          </div>
        </div>
      ) : support.reason === 'blocked' ? (
        // Permission was refused at some point. A site cannot ask again once
        // that has happened - only the person can undo it, from the browser -
        // so offering a button here would be offering one that cannot work.
        <div className="alert alert-warning">
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <strong>{t('misc.blockedHere')}</strong> {t('alerts.blockedBody')}
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {on ? (
            <>
              <button type="button" className="btn btn-outline btn-sm btn-inline" onClick={turnOff} disabled={busy}>
                <BellOff size={16} /> {t('alerts.turnOffHere')}
              </button>
              <button type="button" className="btn btn-ghost btn-sm btn-inline" onClick={sendTest} disabled={busy}>
                <Send size={16} /> {t('alerts.sendTest')}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-primary btn-sm btn-inline" onClick={turnOn} disabled={busy}>
              <Bell size={16} /> {busy ? t('alerts.justAMoment') : t('alerts.turnOn')}
            </button>
          )}
        </div>
      )}

      <p className="hint" style={{ marginTop: 'var(--space-xs)' }}>
        {t('alerts.perDevice')}
      </p>

      <hr className="divider" />

      {/* ---- 2. What to send ---- */}
      <div className="field">
        <label className="row" style={{ gap: 8, cursor: 'pointer' }} htmlFor="alerts-enabled">
          <input
            id="alerts-enabled"
            type="checkbox"
            checked={prefs.enabled}
            onChange={(e) => setPrefs({ ...prefs, enabled: e.target.checked })}
          />
          <span>{t('misc.tellMeNewJob')}</span>
        </label>
      </div>

      {prefs.enabled && (
        <>
          <div className="field">
            <span className="label">{t('alerts.onlyTheseKinds')}</span>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`badge ${prefs.categories.includes(c) ? 'badge-green' : 'badge-grey'}`}
                  onClick={() => toggleCategory(c)}
                >
                  {c}
                </button>
              ))}
            </div>
            <span className="hint">
              {prefs.categories.length === 0
                ? t('alerts.nothingChosen')
                : t('alerts.chosenCount', { count: prefs.categories.length })}
            </span>
          </div>

          <div className="form-row">
            <div className="field">
              <label htmlFor="alerts-minpay">{t('alerts.onlyIfPays')}</label>
              <select
                id="alerts-minpay"
                value={prefs.minPay}
                onChange={(e) => setPrefs({ ...prefs, minPay: e.target.value })}
              >
                <option value="">{t('alerts.anyAmount')}</option>
                <option value="10">€10</option>
                <option value="20">€20</option>
                <option value="30">€30</option>
                <option value="50">€50</option>
              </select>
            </div>

            <div className="field">
              <label htmlFor="alerts-distance">{t('alerts.onlyWithin')}</label>
              <select
                id="alerts-distance"
                value={prefs.maxDistanceKm}
                onChange={(e) => setPrefs({ ...prefs, maxDistanceKm: e.target.value })}
              >
                <option value="">{t('alerts.anyDistance')}</option>
                <option value="2">2 km</option>
                <option value="5">5 km</option>
                <option value="10">10 km</option>
                <option value="20">20 km</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="field">
              <label htmlFor="quiet-from">{t('alerts.quietFrom')}</label>
              <select
                id="quiet-from"
                value={prefs.quietFrom}
                onChange={(e) => setPrefs({ ...prefs, quietFrom: e.target.value })}
              >
                {QUIET_TIMES.map((time) => <option key={time} value={time}>{time}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="quiet-to">{t('alerts.quietUntil')}</label>
              <select
                id="quiet-to"
                value={prefs.quietTo}
                onChange={(e) => setPrefs({ ...prefs, quietTo: e.target.value })}
              >
                {QUIET_TIMES.map((time) => <option key={time} value={time}>{time}</option>)}
              </select>
            </div>
          </div>
        </>
      )}

      <button type="button" className="btn btn-primary btn-inline" onClick={() => save()}>
        {t('alerts.saveWhatIWant')}
      </button>
    </div>
  );
}
